/**
 * Service request detail drawer with client metadata (ClientHeader-style), Timeline, and Payment.
 * Title shows episode service type. Timeline reuses client chart episode timeline with full
 * add-note (chart + admin), view note, and admin note edit/delete.
 */

import {
  Alert,
  Avatar,
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconMail, IconPhone } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ClientSummary, EpisodeRow, CoverageSummary } from '../types/lifecycle';
import { EpisodeTimelineView, NoteDetailView } from '../../clients/components';
import { buildMockAppointmentPageState } from '../../appointments/mockAppointmentPageData';
import {
  mockAdminNotes,
  mockAppointments,
  mockClinicalNotes,
  mockDischargeSummaries,
  mockEpisodes,
} from '../../clients/data';
import type { AdminNote, ClinicalNote } from '../../clients/data';
import {
  CardOnFileSection,
  InsuranceSectionStandalone,
  VerificationsSectionStandalone,
} from '../../../components/billing';
import { useMedplumClientChartBundle } from '../../../hooks/useMedplumClientChartBundle';
import { usePatientCoverageAndPayment } from '../../../hooks/usePatientCoverageAndPayment';
import { TrackerDrawerLiveBilling } from './TrackerDrawerLiveBilling';
import { TransferRequestTab } from './TransferRequestTab';
import type { ServiceRequest } from '@medplum/fhirtypes';

const MOCK_STATE_KEY = 'mockAppointmentData';

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return ((parts[0]?.charAt(0) ?? '') + (parts[parts.length - 1]?.charAt(0) ?? '')).toUpperCase();
  }
  return (name.charAt(0) ?? '?').toUpperCase();
}

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export interface ClientDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  client: ClientSummary | null;
  focusedEpisode: EpisodeRow | null;
  coverages: CoverageSummary[];
  /**
   * When true (default), timeline/payment use demo chart fixtures. When false (live tracker),
   * timeline loads Medplum chart data for `client.id` (Patient id); payment shows Coverage + Stripe cards.
   */
  useMockChartData?: boolean;
  /** Live ServiceRequest for the focused tracker row (transfer preferences, etc.). */
  serviceRequest?: ServiceRequest;
  /** Called after a manual transfer fulfillment completes (refresh tracker data). */
  onTransferComplete?: () => void;
}

export function ClientDetailDrawer({
  open,
  onClose,
  client,
  focusedEpisode,
  coverages: _coverages,
  useMockChartData = true,
  serviceRequest,
  onTransferComplete,
}: ClientDetailDrawerProps): JSX.Element {
  const navigate = useNavigate();
  const serviceType = focusedEpisode?.episodeServiceType;
  const modalTitle = serviceType ? `Service Request for ${serviceType}` : 'Service Request';

  const chartPatientId = !useMockChartData && client?.id ? client.id : undefined;
  const { bundle: liveChart, loading: chartLoading, error: chartError } =
    useMedplumClientChartBundle(chartPatientId);
  const billingLive = usePatientCoverageAndPayment(chartPatientId);

  const timelineEpisodeIdForView = useMemo(() => {
    if (useMockChartData && focusedEpisode) {
      return mockEpisodes.find((e) => e.name === focusedEpisode.episodeServiceType)?.id;
    }
    return focusedEpisode?.episodeOfCareId;
  }, [useMockChartData, focusedEpisode]);

  const showTransferTab = focusedEpisode?.stage === 'transfer';
  const [drawerTab, setDrawerTab] = useState<string>('timeline');

  useEffect(() => {
    if (open) {
      setDrawerTab(showTransferTab ? 'transfer-request' : 'timeline');
    }
  }, [open, showTransferTab, focusedEpisode?.id]);

  const [localNotes, setLocalNotes] = useState<ClinicalNote[]>(mockClinicalNotes);
  const [localAdminNotes, setLocalAdminNotes] = useState<AdminNote[]>(mockAdminNotes);

  const [addNoteOpened, { open: openAddNote, close: closeAddNote }] = useDisclosure(false);
  const [addNoteType, setAddNoteType] = useState<'chart' | 'admin'>('chart');
  const [addChartNoteForm, setAddChartNoteForm] = useState({ title: '', content: '' });
  const [addAdminNoteForm, setAddAdminNoteForm] = useState({
    type: 'Admin' as AdminNote['type'],
    content: '',
  });

  const [selectedNote, setSelectedNote] = useState<ClinicalNote | null>(null);
  const [viewOpened, { open: openView, close: closeView }] = useDisclosure(false);

  const [selectedAdminNote, setSelectedAdminNote] = useState<AdminNote | null>(null);
  const [editAdminNoteOpened, { open: openEditAdminNote, close: closeEditAdminNote }] =
    useDisclosure(false);
  const [deleteAdminNoteOpened, { open: openDeleteAdminNote, close: closeDeleteAdminNote }] =
    useDisclosure(false);
  const [editAdminNoteForm, setEditAdminNoteForm] = useState({
    type: 'Admin' as AdminNote['type'],
    content: '',
  });

  useEffect(() => {
    if (open && client) {
      if (useMockChartData) {
        setLocalNotes([...mockClinicalNotes]);
        setLocalAdminNotes([...mockAdminNotes]);
      } else {
        setLocalNotes([]);
        setLocalAdminNotes([]);
      }
    }
  }, [open, client?.id, useMockChartData]);

  const displayClinicalNotes = useMemo(() => {
    if (useMockChartData) return localNotes;
    return liveChart?.clinicalNotes ?? [];
  }, [useMockChartData, localNotes, liveChart?.clinicalNotes]);

  const displayAdminNotes = useMemo(() => {
    if (useMockChartData) return localAdminNotes;
    return liveChart?.adminNotes ?? [];
  }, [useMockChartData, localAdminNotes, liveChart?.adminNotes]);

  const providerDisplay = client?.name ?? 'Current user';

  const handleOpenAddNote = (type: 'chart' | 'admin') => {
    setAddNoteType(type);
    if (type === 'chart') {
      setAddChartNoteForm({ title: '', content: '' });
    } else {
      setAddAdminNoteForm({ type: 'Admin', content: '' });
    }
    openAddNote();
  };

  const handleSaveChartNote = () => {
    const nowIso = new Date().toISOString();
    const dateStr = nowIso.split('T')[0];
    const title = addChartNoteForm.title.trim() || 'Chart note';
    const newNote: ClinicalNote = {
      id: `note-${makeId()}`,
      type: 'Chart Note',
      noteType: 'Chart Note',
      date: dateStr,
      dateOfService: dateStr,
      provider: providerDisplay,
      providerType: 'Therapist',
      serviceType: 'Clinical Documentation',
      appointmentType: 'N/A',
      serviceRendered: 'Chart Note',
      title,
      status: 'Draft',
      noteStatus: 'Draft',
      billingStatus: 'N/A',
      lastModified: nowIso,
      soapNote: {
        subjective: addChartNoteForm.content.trim(),
        objective: '',
        assessment: '',
        plan: '',
      },
    };
    setLocalNotes((prev) => [newNote, ...prev]);
    closeAddNote();
  };

  const handleSaveAdminNote = () => {
    const nowIso = new Date().toISOString();
    const newAdmin: AdminNote = {
      id: `admin-${makeId()}`,
      content: addAdminNoteForm.content,
      createdBy: providerDisplay,
      createdAt: nowIso,
      updatedAt: nowIso,
      type: addAdminNoteForm.type,
    };
    setLocalAdminNotes((prev) => [newAdmin, ...prev]);
    closeAddNote();
  };

  const handleViewOtherNote = (note: ClinicalNote) => {
    setSelectedNote(note);
    openView();
  };

  const handleCloseView = () => {
    closeView();
    setSelectedNote(null);
  };

  const adminNotesForNote = (note: ClinicalNote | null) =>
    note ? displayAdminNotes.filter((a) => a.noteId === note.id) : [];

  const handleAddAddendum = (noteId: string, content: string) => {
    if (!useMockChartData) return;
    const nowIso = new Date().toISOString();
    setLocalNotes((prev) =>
      prev.map((n) => {
        if (n.id !== noteId) return n;
        const addendums = n.addendums ?? [];
        return {
          ...n,
          addendums: [
            ...addendums,
            { id: `addendum-${makeId()}`, createdAt: nowIso, createdBy: providerDisplay, content },
          ],
        };
      })
    );
  };

  const handleEditAdminNoteOpen = (adminNote: AdminNote) => {
    setSelectedAdminNote(adminNote);
    setEditAdminNoteForm({
      type: adminNote.type,
      content: adminNote.content,
    });
    openEditAdminNote();
  };

  const handleSaveEditAdminNote = () => {
    if (!selectedAdminNote) return;
    const nowIso = new Date().toISOString();
    setLocalAdminNotes((prev) =>
      prev.map((a) =>
        a.id === selectedAdminNote.id ? { ...a, ...editAdminNoteForm, updatedAt: nowIso } : a
      )
    );
    setSelectedAdminNote(null);
    closeEditAdminNote();
  };

  const handleDeleteAdminNoteOpen = (adminNote: AdminNote) => {
    setSelectedAdminNote(adminNote);
    openDeleteAdminNote();
  };

  const confirmDeleteAdminNote = () => {
    if (selectedAdminNote) {
      setLocalAdminNotes((prev) => prev.filter((a) => a.id !== selectedAdminNote.id));
      setSelectedAdminNote(null);
    }
    closeDeleteAdminNote();
  };

  const handleOpenAppointment = (apt: (typeof mockAppointments)[0]) => {
    if (!client) return;
    if (!useMockChartData) {
      navigate(
        `/dashboard/appointments/${apt.id}?patientId=${encodeURIComponent(client.id)}`,
      );
      return;
    }
    const nameParts = client.name.trim().split(/\s+/);
    const minimalClient = {
      id: client.id,
      name: client.name,
      firstName: nameParts[0] ?? '',
      lastName: nameParts.slice(1).join(' ') ?? '',
      preferredFirstName: '',
      preferredLastName: '',
      pronouns: client.pronouns ?? '',
      status: 'Active' as const,
      phone: client.phone,
      email: client.email,
      dateOfBirth: undefined,
      practice: undefined,
      primaryProvider: undefined,
      program: undefined,
      clientType: 'Adult' as const,
      relationshipType: 'Primary' as const,
      firstSession: '',
      mostRecentAppointment: '',
      nextAppointment: '',
      outstandingItems: [],
      provider: '',
    };
    const ep = mockEpisodes.find((e) => e.id === apt.episodeId);
    const episodeDisplayName = ep?.name;
    const mockState = buildMockAppointmentPageState(
      minimalClient as Parameters<typeof buildMockAppointmentPageState>[0],
      apt,
      mockAppointments,
      { episodeDisplayName }
    );
    navigate(`/dashboard/appointments/${apt.id}?patientId=${encodeURIComponent(client.id)}`, {
      state: { [MOCK_STATE_KEY]: mockState },
    });
  };

  if (!client) {
    return (
      <Drawer opened={open} onClose={onClose} position="right" size="xl" title="Service request details">
        <Text c="dimmed">Select a row to view details.</Text>
      </Drawer>
    );
  }

  const timelineClient = {
    id: client.id,
    name: client.name,
    primaryProvider: undefined as string | undefined,
    provider: undefined as string | undefined,
  };

  return (
    <>
      <Drawer
        opened={open}
        onClose={onClose}
        position="right"
        size="xl"
        title={
          <Text component="span" fw={600} size="lg">
            {modalTitle}
          </Text>
        }
      >
        <Stack gap="md">
          {/* Client metadata */}
          <Group align="flex-start" wrap="nowrap">
            <Avatar size="md" radius="md" color="gold">
              {getInitials(client.preferredName ?? client.name)}
            </Avatar>
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Group gap="md" align="flex-end" wrap="wrap">
                <Stack gap={2}>
                  <Text size="xs" c="gray.6" tt="uppercase" fw={600}>
                    Preferred name
                  </Text>
                  <Text size="md" fw={600} style={{ lineHeight: 1.2 }}>
                    {client.preferredName ?? client.name}
                  </Text>
                </Stack>
                <Stack gap={2} style={{ paddingBottom: 6 }}>
                  <Text size="xs" c="gray.6" tt="uppercase" fw={600}>
                    Legal name
                  </Text>
                  <Text size="md" c="gray.6" style={{ lineHeight: 1.2 }}>
                    {client.legalName ?? '—'}
                  </Text>
                </Stack>
                <Group gap="xs" align="center" style={{ paddingBottom: 6 }}>
                  <Text size="md" c="gray.6">
                    ({client.pronouns ?? '—'})
                  </Text>
                  <Badge
                    color={client.active !== false ? 'green' : 'gray'}
                    variant="light"
                    size="sm"
                  >
                    {client.active !== false ? 'ACTIVE' : 'Inactive'}
                  </Badge>
                  {client.isHighRisk && (
                    <Badge color="red" variant="light" size="sm">
                      HIGH RISK
                    </Badge>
                  )}
                </Group>
              </Group>
              <Group gap="md">
                {client.phone && (
                  <Group gap={6}>
                    <IconPhone size={14} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      {client.phone}
                      <Text span size="xs" c="dimmed" ml={4}>
                        [work phone]
                      </Text>
                    </Text>
                  </Group>
                )}
                {client.email && (
                  <Group gap={6}>
                    <IconMail size={14} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      {client.email}
                      <Text span size="xs" c="dimmed" ml={4}>
                        [work email]
                      </Text>
                    </Text>
                  </Group>
                )}
              </Group>
            </Stack>
          </Group>

          <Tabs value={drawerTab} onChange={(v) => v && setDrawerTab(v)}>
            <Tabs.List>
              <Tabs.Tab value="timeline">Timeline</Tabs.Tab>
              <Tabs.Tab value="payment">Payment</Tabs.Tab>
              {showTransferTab && (
                <Tabs.Tab value="transfer-request">Transfer request</Tabs.Tab>
              )}
            </Tabs.List>

            <Tabs.Panel value="payment" pt="md">
              {useMockChartData ? (
                <Stack gap="lg">
                  <CardOnFileSection />
                  <InsuranceSectionStandalone />
                  <VerificationsSectionStandalone />
                </Stack>
              ) : (
                <TrackerDrawerLiveBilling
                  loading={billingLive.loading}
                  error={billingLive.error}
                  paymentMethods={billingLive.paymentMethods}
                  coverages={billingLive.coverages}
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="timeline" pt="md">
              {!useMockChartData && chartLoading && (
                <Group justify="center" py="md" gap="sm">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">
                    Loading medical record…
                  </Text>
                </Group>
              )}
              {!useMockChartData && chartError && (
                <Alert color="red" title="Could not load chart" mb="md">
                  {chartError.message}
                </Alert>
              )}
              <EpisodeTimelineView
                client={timelineClient}
                appointments={
                  useMockChartData ? mockAppointments : (liveChart?.appointments ?? [])
                }
                notes={displayClinicalNotes}
                adminNotes={displayAdminNotes}
                episodes={useMockChartData ? mockEpisodes : (liveChart?.episodes ?? [])}
                dischargeSummaries={
                  useMockChartData ? mockDischargeSummaries : (liveChart?.dischargeSummaries ?? [])
                }
                episodeId={timelineEpisodeIdForView ?? undefined}
                showAddNote={useMockChartData}
                onOpenAppointment={handleOpenAppointment}
                onViewNote={handleViewOtherNote}
                onOpenAddNote={useMockChartData ? handleOpenAddNote : () => {}}
                onEditAdminNote={useMockChartData ? handleEditAdminNoteOpen : () => {}}
                onDeleteAdminNote={useMockChartData ? handleDeleteAdminNoteOpen : () => {}}
              />
              {!useMockChartData &&
                !chartLoading &&
                !chartError &&
                (liveChart?.appointments?.length ?? 0) === 0 &&
                (liveChart?.clinicalNotes?.length ?? 0) === 0 && (
                  <Text size="sm" c="dimmed" mt="md">
                    No appointments or documentation in the chart yet for this client.
                  </Text>
                )}
            </Tabs.Panel>

            {showTransferTab && focusedEpisode && (
              <Tabs.Panel value="transfer-request" pt="md">
                <TransferRequestTab
                  episode={focusedEpisode}
                  serviceRequest={serviceRequest}
                  useMockData={useMockChartData}
                  onTransferComplete={onTransferComplete}
                />
              </Tabs.Panel>
            )}
          </Tabs>
        </Stack>
      </Drawer>

      {/* View chart note modal */}
      <Modal
        opened={viewOpened}
        onClose={handleCloseView}
        title="Chart note"
        size="xl"
        styles={{ content: { border: '1px solid #9CA3AF' } }}
      >
        {selectedNote && (
          <NoteDetailView
            note={displayClinicalNotes.find((n) => n.id === selectedNote.id) ?? selectedNote}
            adminNotes={adminNotesForNote(selectedNote)}
            onAddAddendum={useMockChartData ? handleAddAddendum : undefined}
            onEditAdminNote={useMockChartData ? handleEditAdminNoteOpen : undefined}
            onDeleteAdminNote={useMockChartData ? handleDeleteAdminNoteOpen : undefined}
            footer={
              <Group justify="flex-end" mt="md">
                <Button onClick={handleCloseView}>Close</Button>
              </Group>
            }
          />
        )}
      </Modal>

      {/* Add note modal (chart or admin) */}
      <Modal
        opened={addNoteOpened}
        onClose={closeAddNote}
        title={addNoteType === 'chart' ? 'Add chart note' : 'Add admin note'}
        size="lg"
      >
        {addNoteType === 'chart' ? (
          <Stack gap="md">
            <Text size="sm" c="gray.6">
              Add a chart note not tied to an appointment. Use for documentation, follow-ups, or other
              clinical entries.
            </Text>
            <TextInput
              label="Title (optional)"
              placeholder="e.g. Follow-up, Phone call note"
              value={addChartNoteForm.title}
              onChange={(e) =>
                setAddChartNoteForm((prev) => ({ ...prev, title: e.currentTarget.value }))
              }
            />
            <Textarea
              label="Content"
              placeholder="Enter note content..."
              value={addChartNoteForm.content}
              onChange={(e) =>
                setAddChartNoteForm((prev) => ({ ...prev, content: e.currentTarget.value }))
              }
              minRows={4}
              required
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={closeAddNote}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveChartNote}
                disabled={!addChartNoteForm.content.trim()}
              >
                Save note
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap="md">
            <Select
              label="Type"
              data={[
                { value: 'Admin', label: 'Admin' },
                { value: 'Client Communication', label: 'Client Communication' },
                { value: 'Internal', label: 'Internal' },
                { value: 'Reminder', label: 'Reminder' },
              ]}
              value={addAdminNoteForm.type}
              onChange={(v) =>
                setAddAdminNoteForm((prev) => ({
                  ...prev,
                  type: (v as AdminNote['type']) ?? 'Admin',
                }))
              }
            />
            <Textarea
              label="Content"
              placeholder="Enter note content..."
              value={addAdminNoteForm.content}
              onChange={(e) =>
                setAddAdminNoteForm((prev) => ({ ...prev, content: e.currentTarget.value }))
              }
              minRows={4}
              required
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={closeAddNote}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveAdminNote}
                disabled={!addAdminNoteForm.content.trim()}
              >
                Save admin note
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Edit admin note modal */}
      <Modal
        opened={editAdminNoteOpened}
        onClose={() => {
          setSelectedAdminNote(null);
          closeEditAdminNote();
        }}
        title="Edit admin note"
        size="lg"
      >
        {selectedAdminNote && (
          <Stack gap="md">
            <Select
              label="Type"
              data={[
                { value: 'Admin', label: 'Admin' },
                { value: 'Client Communication', label: 'Client Communication' },
                { value: 'Internal', label: 'Internal' },
                { value: 'Reminder', label: 'Reminder' },
              ]}
              value={editAdminNoteForm.type}
              onChange={(v) =>
                setEditAdminNoteForm((prev) => ({
                  ...prev,
                  type: (v as AdminNote['type']) ?? 'Admin',
                }))
              }
            />
            <Textarea
              label="Content"
              value={editAdminNoteForm.content}
              onChange={(e) =>
                setEditAdminNoteForm((prev) => ({ ...prev, content: e.currentTarget.value }))
              }
              minRows={4}
              required
            />
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  setSelectedAdminNote(null);
                  closeEditAdminNote();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEditAdminNote}
                disabled={!editAdminNoteForm.content.trim()}
              >
                Save changes
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete admin note confirmation */}
      <Modal
        opened={deleteAdminNoteOpened}
        onClose={closeDeleteAdminNote}
        title="Delete admin note"
        size="sm"
        centered
      >
        {selectedAdminNote && (
          <Stack gap="md">
            <Text size="sm" c="gray.7">
              Are you sure you want to delete this admin note?
            </Text>
            <Group justify="flex-end">
              <Button onClick={closeDeleteAdminNote}>Cancel</Button>
              <Button color="red" onClick={confirmDeleteAdminNote}>
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
